import { computed } from 'vue'
import type { CRMState } from './state'
import type { Prospect, Activity, ProspectStage, ActivityType } from '@/types'

export const createGetters = (state: CRMState) => {
  const filteredProspects = computed(() => {
    let result = [...state.prospects]

    // Filter by search
    if (state.filters.search) {
      const search = state.filters.search.toLowerCase()
      result = result.filter(
        (prospect) =>
          prospect.companyName.toLowerCase().includes(search) ||
          prospect.contactName.toLowerCase().includes(search) ||
          prospect.email.toLowerCase().includes(search)
      )
    }

    // Filter by stage
    if (state.filters.stage) {
      result = result.filter((prospect) => prospect.stage === state.filters.stage)
    }

    // Filter by assigned to
    if (state.filters.assignedTo) {
      result = result.filter((prospect) => prospect.assignedTo === state.filters.assignedTo)
    }

    // Filter by source
    if (state.filters.source) {
      result = result.filter((prospect) => prospect.source === state.filters.source)
    }

    return result
  })

  const prospectsByStage = computed(() => {
    const grouped: Record<ProspectStage, Prospect[]> = {
      prospect: [],
      contacted: [],
      proposal_sent: [],
      negotiation: [],
      won: [],
      lost: [],
    }

    state.prospects.forEach((prospect) => {
      if (grouped[prospect.stage]) {
        grouped[prospect.stage].push(prospect)
      }
    })

    Object.values(grouped).forEach(arr =>
      arr.sort((a, b) => (a.boardOrder ?? Infinity) - (b.boardOrder ?? Infinity))
    )

    return grouped
  })

  const pipelineProspects = computed(() => {
    // Only show active pipeline stages (exclude won/lost)
    const stages: ProspectStage[] = ['prospect', 'contacted', 'proposal_sent', 'negotiation']
    const grouped: Record<ProspectStage, Prospect[]> = {
      prospect: [],
      contacted: [],
      proposal_sent: [],
      negotiation: [],
      won: [],
      lost: [],
    }

    state.prospects.forEach((prospect) => {
      if (stages.includes(prospect.stage) && grouped[prospect.stage]) {
        grouped[prospect.stage].push(prospect)
      }
    })

    Object.values(grouped).forEach(arr =>
      arr.sort((a, b) => (a.boardOrder ?? Infinity) - (b.boardOrder ?? Infinity))
    )

    return grouped
  })

  const activitiesByType = computed(() => {
    const grouped: Record<ActivityType, Activity[]> = {
      call: [],
      email: [],
      meeting: [],
      note: [],
    }

    state.activities.forEach((activity) => {
      if (grouped[activity.type]) {
        grouped[activity.type].push(activity)
      }
    })

    return grouped
  })

  const stats = computed(() => {
    const total = state.prospects.length
    const active = state.prospects.filter(
      (p) => p.stage !== 'won' && p.stage !== 'lost'
    ).length
    const won = state.prospects.filter((p) => p.stage === 'won').length
    const lost = state.prospects.filter((p) => p.stage === 'lost').length
    const totalValue = state.prospects
      .filter((p) => p.stage !== 'lost')
      .reduce((sum, p) => sum + (p.estimatedValue || 0), 0)
    const wonValue = state.prospects
      .filter((p) => p.stage === 'won')
      .reduce((sum, p) => sum + (p.estimatedValue || 0), 0)
    const conversionRate = total > 0 ? Math.round((won / total) * 100) : 0

    return {
      total,
      active,
      won,
      lost,
      totalValue,
      wonValue,
      conversionRate,
    }
  })

  const pipelineStats = computed(() => {
    const stages: ProspectStage[] = ['prospect', 'contacted', 'proposal_sent', 'negotiation']

    return stages.map((stage) => {
      const prospects = state.prospects.filter((p) => p.stage === stage)
      return {
        stage,
        count: prospects.length,
        value: prospects.reduce((sum, p) => sum + (p.estimatedValue || 0), 0),
      }
    })
  })

  const uniqueSources = computed(() => {
    const sources = new Set<string>()
    state.prospects.forEach((p) => {
      if (p.source) sources.add(p.source)
    })
    return Array.from(sources).sort()
  })

  const getProspectById = (id: string): Prospect | undefined => {
    return state.prospects.find((prospect) => prospect.id === id)
  }

  const getActivitiesByProspectId = (prospectId: string): Activity[] => {
    return state.activities.filter((activity) => activity.prospectId === prospectId)
  }

  const recentActivities = computed(() => {
    return [...state.activities]
      .sort((a, b) => b.date.getTime() - a.date.getTime())
      .slice(0, 10)
  })

  return {
    filteredProspects,
    prospectsByStage,
    pipelineProspects,
    activitiesByType,
    stats,
    pipelineStats,
    uniqueSources,
    getProspectById,
    getActivitiesByProspectId,
    recentActivities,
  }
}
